import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { QuizMetadataForm } from './QuizMetadataForm';
import { QuestionList } from './QuestionList';
import { QuestionEditorPanel } from './QuestionEditorPanel';
import { QuestionPreviewPanel } from './QuestionPreviewPanel';
import { DraftIndicator } from './DraftIndicator';
import { SaveQuizButton } from './SaveQuizButton';
import { ResumeDraftModal } from './ResumeDraftModal';
import { Confetti } from '@/components/Confetti';
import { useDraftManager } from '@/hooks/useDraftManager';
import { useUserTier } from '@/queries/subscriptions';
import type { NewQuestion, Difficulty } from '@/types';
import styles from './QuizBuilder.module.scss';

interface QuizBuilderProps {
  /**
   * If provided, we're in edit mode updating an existing quizlet
   */
  existingQuizletId?: string;
  initialData?: {
    name: string;
    original_prompt: string;
    is_daily_knowledge: boolean;
    questions: NewQuestion[];
  };
  onSave?: (data: QuizBuilderData) => Promise<void>;
  onCancel?: () => void;
}

export interface QuizBuilderData {
  name: string;
  original_prompt: string;
  is_daily_knowledge: boolean;
  questions: NewQuestion[];
}

export function QuizBuilder({
  existingQuizletId,
  initialData,
  onSave,
  onCancel,
}: QuizBuilderProps) {
  // Quiz metadata state
  const [name, setName] = useState(initialData?.name || '');
  const [originalPrompt, setOriginalPrompt] = useState(initialData?.original_prompt || '');
  const [isDailyKnowledge, setIsDailyKnowledge] = useState(initialData?.is_daily_knowledge || false);

  const userTier = useUserTier();
  const showMath = userTier !== 'foundation';

  // Questions state
  const [questions, setQuestions] = useState<NewQuestion[]>(initialData?.questions || []);

  // Editor state
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Partial<NewQuestion> | null>(null);
  const [isEditMode, setIsEditMode] = useState(false); // true when editing existing question

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const isValid = name.trim() !== '' && questions.length > 0;

  // Current quiz data for draft management
  const currentQuizData = useMemo(
    () => ({
      name,
      original_prompt: originalPrompt,
      is_daily_knowledge: isDailyKnowledge,
      questions,
    }),
    [name, originalPrompt, isDailyKnowledge, questions]
  );

  // Draft management
  const {
    hasDraft,
    loadDraft,
    clearDraft,
    getDraftMetadata,
    lastAutoSaved,
  } = useDraftManager(
    currentQuizData,
    !!existingQuizletId,
    existingQuizletId
  );

  // Show draft modal on mount if draft exists
  useEffect(() => {
    if (hasDraft && !initialData) {
      setShowDraftModal(true);
    }
  }, [hasDraft, initialData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isValid && !isSaving) {
          handleSaveQuiz();
        }
      }

      // Ctrl+Enter or Cmd+Enter to add new question
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleAddNewQuestion();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isValid, isSaving]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unsaved changes warning
  const hasUnsavedChanges = useMemo(() => {
    if (!initialData) {
      // For new quizzes, check if user has made any changes
      return name.trim() !== '' || questions.length > 0;
    }
    // For edit mode, check if data has changed
    return (
      name !== initialData.name ||
      originalPrompt !== initialData.original_prompt ||
      isDailyKnowledge !== initialData.is_daily_knowledge ||
      JSON.stringify(questions) !== JSON.stringify(initialData.questions)
    );
  }, [name, originalPrompt, isDailyKnowledge, questions, initialData]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle resume draft
  const handleResumeDraft = () => {
    const draft = loadDraft();
    if (draft) {
      setName(draft.name);
      setOriginalPrompt(draft.original_prompt);
      setIsDailyKnowledge(draft.is_daily_knowledge);
      setQuestions(draft.questions);
    }
    setShowDraftModal(false);
  };

  // Handle discard draft
  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftModal(false);
  };

  // Initialize editor with first question if in edit mode
  useEffect(() => {
    if (initialData?.questions && initialData.questions.length > 0 && selectedQuestionIndex === null) {
      setSelectedQuestionIndex(0);
      setEditingQuestion(initialData.questions[0]);
      setIsEditMode(true);
    }
  }, [initialData, selectedQuestionIndex]);

  const handleAddNewQuestion = () => {
    // Create a new blank question template
    const newQuestion: Partial<NewQuestion> = {
      question_text: '',
      question_type: 'multiple_choice',
      content: {
        type: 'multiple_choice',
        options: [],
        allowMultipleAnswers: false,
      },
      explanation: '',
      difficulty: 'medium' as Difficulty,
      order_index: questions.length,
      tags: [],
    };

    setEditingQuestion(newQuestion);
    setSelectedQuestionIndex(null); // Not in list yet
    setIsEditMode(false);
  };

  const handleEditQuestion = (index: number) => {
    setSelectedQuestionIndex(index);
    setEditingQuestion(questions[index]);
    setIsEditMode(true);
  };

  const handleDeleteQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    // Reindex remaining questions
    const reindexed = updatedQuestions.map((q, i) => ({ ...q, order_index: i }));
    setQuestions(reindexed);

    // Clear editor if we were editing the deleted question
    if (selectedQuestionIndex === index) {
      setSelectedQuestionIndex(null);
      setEditingQuestion(null);
      setIsEditMode(false);
    }
  };

  const handleSaveQuestion = (question: NewQuestion) => {
    if (isEditMode && selectedQuestionIndex !== null) {
      // Update existing question
      const updated = [...questions];
      updated[selectedQuestionIndex] = question;
      setQuestions(updated);
    } else {
      // Add new question
      const newQuestion = { ...question, order_index: questions.length };
      setQuestions([...questions, newQuestion]);
    }

    // Clear editor
    setEditingQuestion(null);
    setSelectedQuestionIndex(null);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setSelectedQuestionIndex(null);
    setIsEditMode(false);
  };

  const handleReorderQuestions = (reorderedQuestions: NewQuestion[]) => {
    // Update order_index for all questions
    const reindexed = reorderedQuestions.map((q, i) => ({ ...q, order_index: i }));
    setQuestions(reindexed);
  };

  const handleSaveQuiz = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      await onSave({
        name,
        original_prompt: originalPrompt,
        is_daily_knowledge: isDailyKnowledge,
        questions,
      });
      // setLastSaved(new Date());
      // Clear draft on successful save
      clearDraft();
      // Show confetti celebration
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000); // Hide after 4 seconds
    } catch (error) {
      console.error('Error saving quiz:', error);
    } finally {
      setIsSaving(false);
    }
  };


  // Get draft metadata for modal
  const draftMetadata = getDraftMetadata();

  return (
    <div className={styles.quizBuilder}>
      {/* Draft Resume Modal */}
      <ResumeDraftModal
        isOpen={showDraftModal}
        lastSaved={draftMetadata?.lastSaved || null}
        questionCount={draftMetadata?.questionCount || 0}
        onResume={handleResumeDraft}
        onDiscard={handleDiscardDraft}
      />

      {/* Confetti Celebration */}
      <AnimatePresence>
        {showConfetti && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            <Confetti />
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.quizBuilder__header}>
        <h1 className={styles.quizBuilder__title}>
          {existingQuizletId ? 'Edit Quiz' : 'Create Quiz'}
        </h1>
        <div className={styles.quizBuilder__headerActions}>
          <DraftIndicator lastSaved={lastAutoSaved} />
          <SaveQuizButton
            onSave={handleSaveQuiz}
            isValid={isValid}
            isSaving={isSaving}
          />
          {onCancel && (
            <motion.button
              className={styles.quizBuilder__cancelButton}
              onClick={onCancel}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
          )}
        </div>
      </div>

      {/* Metadata Form */}
      <QuizMetadataForm
        name={name}
        originalPrompt={originalPrompt}
        isDailyKnowledge={isDailyKnowledge}
        onNameChange={setName}
        onPromptChange={setOriginalPrompt}
        onDailyKnowledgeChange={setIsDailyKnowledge}
      />

      {/* Main Content Area */}
      <div className={styles.quizBuilder__content}>
        {/* Left Panel: Editor */}
        <div className={styles.quizBuilder__editorPanel}>
          <QuestionEditorPanel
            question={editingQuestion}
            onSave={handleSaveQuestion}
            onCancel={handleCancelEdit}
            isEditMode={isEditMode}
            showMath={showMath}
          />
        </div>

        {/* Right Panel: Preview */}
        <div className={styles.quizBuilder__previewPanel}>
          <QuestionPreviewPanel question={editingQuestion} />
        </div>
      </div>

      {/* Question List */}
      <div className={styles.quizBuilder__questionList}>
        <QuestionList
          questions={questions}
          selectedIndex={selectedQuestionIndex}
          onAdd={handleAddNewQuestion}
          onEdit={handleEditQuestion}
          onDelete={handleDeleteQuestion}
          onReorder={handleReorderQuestions}
        />
      </div>
    </div>
  );
}
